/**
 * <shoppable-image>
 * ---------------------------------------------------------------------------
 * Powers the "Shoppable image" and "Shoppable image duo" sections.
 *
 * Expects this markup contract (shared by both sections):
 *   [data-hotspot]          wrapper positioned over the image
 *   [data-hotspot-trigger]  the pin button
 *   [data-hotspot-popup]    the product popup, toggled with .is-active
 *   [data-hotspot-close]    close button inside the popup
 *   [data-shoppable-form]   optional add-to-cart form (needs a .add-to-cart submit)
 *
 * Add to cart hands off to Impulse's theme.AjaxProduct, which posts to
 * cart/add.js and dispatches `ajaxProduct:added` — the event the cart drawer
 * already listens for.
 */
(function () {
  if (customElements.get('shoppable-image')) return;

  class ShoppableImage extends HTMLElement {
    connectedCallback() {
      this.hotspots = Array.from(this.querySelectorAll('[data-hotspot]'));
      if (!this.hotspots.length) return;

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      // connectedCallback fires again if the element is moved in the DOM;
      // only wire the per-hotspot listeners once.
      if (this.initialized) return;
      this.initialized = true;

      this.hotspots.forEach((hotspot) => this.bindHotspot(hotspot));
      this.initCartForms();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    bindHotspot(hotspot) {
      const trigger = hotspot.querySelector('[data-hotspot-trigger]');
      const popup = hotspot.querySelector('[data-hotspot-popup]');
      if (!trigger || !popup) return;

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const willOpen = !popup.classList.contains('is-active');
        this.closeAll();
        if (willOpen) this.open(trigger, popup);
      });

      const closeButton = popup.querySelector('[data-hotspot-close]');
      if (closeButton) {
        closeButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.close(trigger, popup);
          trigger.focus();
        });
      }
    }

    open(trigger, popup) {
      popup.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    }

    close(trigger, popup) {
      popup.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    }

    closeAll() {
      this.hotspots.forEach((hotspot) => {
        const trigger = hotspot.querySelector('[data-hotspot-trigger]');
        const popup = hotspot.querySelector('[data-hotspot-popup]');
        if (trigger && popup) this.close(trigger, popup);
      });
    }

    handleDocumentClick(event) {
      const target = event.target;

      if (target && typeof target.closest === 'function') {
        const hotspot = target.closest('[data-hotspot]');
        // Only ignore clicks inside *this* element's hotspots. A click on a
        // second shoppable section on the page should still close ours.
        if (hotspot && this.contains(hotspot)) return;
      }

      this.closeAll();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      const openHotspot = this.hotspots.find((hotspot) => {
        const popup = hotspot.querySelector('[data-hotspot-popup]');
        return popup && popup.classList.contains('is-active');
      });

      this.closeAll();

      if (openHotspot) {
        const trigger = openHotspot.querySelector('[data-hotspot-trigger]');
        if (trigger) trigger.focus();
      }
    }

    initCartForms() {
      const forms = Array.from(this.querySelectorAll('[data-shoppable-form]'));
      if (!forms.length) return;

      const bind = () => {
        if (!window.theme || !theme.AjaxProduct) return;

        forms.forEach((form) => {
          new theme.AjaxProduct(form);

          // The cart drawer opens on success, so get the popup out of the way.
          form.addEventListener('ajaxProduct:added', () => {
            const hotspot = form.closest('[data-hotspot]');
            if (!hotspot) return;
            const trigger = hotspot.querySelector('[data-hotspot-trigger]');
            const popup = hotspot.querySelector('[data-hotspot-popup]');
            if (trigger && popup) this.close(trigger, popup);
          });
        });
      };

      // theme.js is deferred, so it has not run yet during initial parse.
      if (window.theme && theme.AjaxProduct) {
        bind();
      } else {
        document.addEventListener('DOMContentLoaded', bind, { once: true });
      }
    }
  }

  customElements.define('shoppable-image', ShoppableImage);
})();
